import {
  ChatParticipantType,
  ConnectionStatus,
  EntityModel,
  MeetingStatus,
  MeetingType,
  ParticipantType,
  reportingPrisma,
} from "client";
import type {
  EventTierAnalyticsDbFilters,
  EventTierBookmarksMeetingsStats,
  EventTierCompanyPerformerStats,
  EventTierNetworkingStats,
} from "./types";
import { analyticsCreatedAt, withCreatedAt } from "./prisma-date-range";

const IN_BATCH = 500;
const TOP_PERFORMERS = 10;

type CreatedAtRange = Extract<
  ReturnType<typeof analyticsCreatedAt>,
  { mode: "bounded" | "unbounded" }
>;

function firstName(
  translations: Array<{ name: string }> | undefined
): string | undefined {
  const name = translations?.[0]?.name?.trim();
  return name ? name : undefined;
}

async function mapInBatches<T, R>(
  ids: T[],
  mapper: (batch: T[]) => Promise<R>
): Promise<R[]> {
  const results: R[] = [];
  for (let i = 0; i < ids.length; i += IN_BATCH) {
    results.push(await mapper(ids.slice(i, i + IN_BATCH)));
  }
  return results;
}

function emptyNetworkingStats(
  filters: EventTierAnalyticsDbFilters
): EventTierNetworkingStats {
  return {
    profileView: filters.networkingProfile ?? "company",
    connectionsAccepted: 0,
    exhibitorsConfigured: 0,
    requestsSent: 0,
    requestsAccepted: 0,
    messagesSent: 0,
    topPerformers: [],
  };
}

function emptyBookmarksMeetingsStats(): EventTierBookmarksMeetingsStats {
  return {
    totalBookmarks: 0,
    topSessionCount: 0,
    topCompanyCount: 0,
    meetingsScheduled: 0,
    meetingsAccepted: 0,
    meetingsRequested: 0,
  };
}

function createdAtWhereFor(range: CreatedAtRange) {
  return <T extends Record<string, unknown>>(where: T) =>
    withCreatedAt(where, range);
}

function withStartDate<T extends Record<string, unknown>>(
  where: T,
  range: CreatedAtRange
): T {
  if (range.mode === "bounded") {
    return { ...where, startDate: range.createdAt };
  }
  return where;
}

function networkingMeetingWhere(eventId: string, range: CreatedAtRange) {
  return withStartDate(
    {
      eventId,
      type: { not: MeetingType.SESSION },
    },
    range
  );
}

async function loadNetworkingMeetingStats(
  eventId: string,
  range: CreatedAtRange
) {
  const where = networkingMeetingWhere(eventId, range);
  const [meetingsRequested, meetingsAccepted] = await Promise.all([
    reportingPrisma.meeting.count({ where }),
    reportingPrisma.meeting.count({
      where: { ...where, status: MeetingStatus.ACCEPTED },
    }),
  ]);

  return {
    meetingsRequested,
    meetingsAccepted,
    meetingsScheduled: meetingsAccepted,
  };
}

export async function queryEventTierNetworkingStats(
  eventId: string,
  filters: EventTierAnalyticsDbFilters
): Promise<EventTierNetworkingStats> {
  const range = analyticsCreatedAt(filters);
  if (range.mode === "match-none") {
    return emptyNetworkingStats(filters);
  }
  if ((filters.networkingProfile ?? "company") === "individual") {
    return queryIndividualNetworking(eventId, range);
  }
  return queryCompanyNetworking(eventId, range);
}

export async function queryEventTierBookmarksMeetingsStats(
  eventId: string,
  filters: EventTierAnalyticsDbFilters
): Promise<EventTierBookmarksMeetingsStats> {
  const range = analyticsCreatedAt(filters);
  if (range.mode === "match-none") {
    return emptyBookmarksMeetingsStats();
  }

  const createdAtWhere = createdAtWhereFor(range);
  const [companies, totalBookmarks, sessionBookmarkGroups, meetingStats] =
    await Promise.all([
      loadCompanies(eventId),
      reportingPrisma.bookmark.count({
        where: createdAtWhere({ eventId }),
      }),
      reportingPrisma.bookmark.groupBy({
        by: ["entityId"],
        where: createdAtWhere({
          eventId,
          entityModel: EntityModel.SESSION,
        }),
        _count: { _all: true },
      }),
      loadNetworkingMeetingStats(eventId, range),
    ]);

  const companyIds = [
    ...companies.exhibitorById.keys(),
    ...companies.sponsorById.keys(),
  ];
  const companyBookmarkGroups =
    companyIds.length === 0
      ? []
      : (
          await mapInBatches(companyIds, (batch) =>
            reportingPrisma.bookmark.groupBy({
              by: ["entityId"],
              where: createdAtWhere({
                eventId,
                entityModel: EntityModel.PROFILE,
                entityId: { in: batch },
              }),
              _count: { _all: true },
            })
          )
        ).flat();

  const topSession = pickTopGroup(sessionBookmarkGroups);
  const topCompany = pickTopGroup(companyBookmarkGroups);

  return {
    totalBookmarks,
    topSessionCount: topSession?.count ?? 0,
    topSessionName: topSession
      ? firstName(
          (
            await reportingPrisma.sessionV2.findUnique({
              where: { id: topSession.entityId },
              select: { translations: { select: { name: true }, take: 1 } },
            })
          )?.translations
        )
      : undefined,
    topCompanyCount: topCompany?.count ?? 0,
    topCompanyName: topCompany
      ? companies.exhibitorById.get(topCompany.entityId) ??
        companies.sponsorById.get(topCompany.entityId)
      : undefined,
    meetingsScheduled: meetingStats.meetingsScheduled,
    meetingsAccepted: meetingStats.meetingsAccepted,
    meetingsRequested: meetingStats.meetingsRequested,
  };
}

async function queryIndividualNetworking(
  eventId: string,
  range: CreatedAtRange
): Promise<EventTierNetworkingStats> {
  const createdAtWhere = createdAtWhereFor(range);
  const [requestsSent, requestsAccepted, chats] = await Promise.all([
    reportingPrisma.connectionRequest.count({
      where: createdAtWhere({
        eventId,
        senderType: ParticipantType.USER,
        receiverType: ParticipantType.USER,
      }),
    }),
    reportingPrisma.connectionRequest.count({
      where: createdAtWhere({
        eventId,
        senderType: ParticipantType.USER,
        receiverType: ParticipantType.USER,
        status: ConnectionStatus.ACCEPTED,
      }),
    }),
    reportingPrisma.chat.findMany({
      where: { eventId },
      select: { id: true },
    }),
  ]);

  const messagesSent = await countMessages(
    chats.map((chat) => chat.id),
    range
  );

  return {
    profileView: "individual",
    connectionsAccepted: requestsAccepted,
    exhibitorsConfigured: 0,
    requestsSent,
    requestsAccepted,
    messagesSent,
    topPerformers: [],
  };
}

async function queryCompanyNetworking(
  eventId: string,
  range: CreatedAtRange
): Promise<EventTierNetworkingStats> {
  const createdAtWhere = createdAtWhereFor(range);
  const [companies, requestsAccepted, chats, requestMeetings] =
    await Promise.all([
      loadCompanies(eventId),
      reportingPrisma.connectionRequest.count({
        where: createdAtWhere({
          eventId,
          status: ConnectionStatus.ACCEPTED,
        }),
      }),
      reportingPrisma.chat.findMany({
        where: { eventId },
        select: { id: true },
      }),
      reportingPrisma.meeting.findMany({
        where: withStartDate(
          {
            eventId,
            type: { not: MeetingType.SESSION },
            status: { not: MeetingStatus.REJECTED },
          },
          range
        ),
        select: { id: true },
      }),
    ]);

  const chatIds = chats.map((chat) => chat.id);
  const [messageCounts, meetingParticipants] = await Promise.all([
    countCompanyMessages(chatIds, range),
    loadCompanyMeetingParticipants(
      requestMeetings.map((meeting) => meeting.id)
    ),
  ]);

  const meetingsByCompany = new Map<string, number>();
  for (const participant of meetingParticipants) {
    meetingsByCompany.set(
      participant.entityId,
      (meetingsByCompany.get(participant.entityId) ?? 0) + 1
    );
  }

  const performerIds = new Set([
    ...meetingsByCompany.keys(),
    ...messageCounts.keys(),
  ]);
  const topPerformers: EventTierCompanyPerformerStats[] = [...performerIds]
    .flatMap((id) => {
      const isExhibitor = companies.exhibitorById.has(id);
      const isSponsor = companies.sponsorById.has(id);
      if (!isExhibitor && !isSponsor) return [];
      return [
        {
          company: isExhibitor
            ? companies.exhibitorById.get(id)!
            : companies.sponsorById.get(id)!,
          type: isExhibitor ? ("Exhibitor" as const) : ("Sponsor" as const),
          meetings: meetingsByCompany.get(id) ?? 0,
          messages: messageCounts.get(id) ?? 0,
        },
      ];
    })
    .sort((a, b) => b.meetings + b.messages - (a.meetings + a.messages))
    .slice(0, TOP_PERFORMERS);

  return {
    profileView: "company",
    connectionsAccepted: requestsAccepted,
    exhibitorsConfigured:
      companies.exhibitorById.size + companies.sponsorById.size,
    requestsSent: 0,
    requestsAccepted,
    messagesSent: 0,
    topPerformers,
  };
}

async function loadCompanies(eventId: string) {
  const [exhibitors, sponsors] = await Promise.all([
    reportingPrisma.exhibitor.findMany({
      where: { eventId },
      select: {
        id: true,
        translations: { select: { name: true }, take: 1 },
      },
    }),
    reportingPrisma.sponsor.findMany({
      where: { eventId },
      select: {
        id: true,
        translations: { select: { name: true }, take: 1 },
      },
    }),
  ]);

  return {
    exhibitorById: new Map(
      exhibitors.map((row) => [
        row.id,
        firstName(row.translations) ?? "Exhibitor",
      ])
    ),
    sponsorById: new Map(
      sponsors.map((row) => [row.id, firstName(row.translations) ?? "Sponsor"])
    ),
  };
}

function pickTopGroup(
  groups: Array<{ entityId: string; _count: { _all: number } }>
) {
  return groups.reduce<{ entityId: string; count: number } | null>(
    (best, group) => {
      const count = group._count._all;
      if (!best || count > best.count) {
        return { entityId: group.entityId, count };
      }
      return best;
    },
    null
  );
}

async function countMessages(
  chatIds: string[],
  range: CreatedAtRange
): Promise<number> {
  if (chatIds.length === 0) return 0;
  const counts = await mapInBatches(chatIds, (batch) =>
    reportingPrisma.message.count({
      where: withCreatedAt(
        {
          chatId: { in: batch },
          senderType: ChatParticipantType.USER,
        },
        range
      ),
    })
  );
  return counts.reduce((sum, count) => sum + count, 0);
}

async function countCompanyMessages(
  chatIds: string[],
  range: CreatedAtRange
): Promise<Map<string, number>> {
  const totals = new Map<string, number>();
  if (chatIds.length === 0) return totals;

  const groups = await mapInBatches(chatIds, (batch) =>
    reportingPrisma.message.groupBy({
      by: ["senderId"],
      where: withCreatedAt(
        {
          chatId: { in: batch },
          senderType: {
            in: [ChatParticipantType.EXHIBITOR, ChatParticipantType.SPONSOR],
          },
        },
        range
      ),
      _count: { _all: true },
    })
  );

  for (const batch of groups) {
    for (const group of batch) {
      totals.set(
        group.senderId,
        (totals.get(group.senderId) ?? 0) + group._count._all
      );
    }
  }
  return totals;
}

async function loadCompanyMeetingParticipants(meetingIds: string[]) {
  if (meetingIds.length === 0) return [];
  const batches = await mapInBatches(meetingIds, (batch) =>
    reportingPrisma.meetingParticipant.findMany({
      where: {
        meetingId: { in: batch },
        entityType: {
          in: [ParticipantType.EXHIBITOR, ParticipantType.SPONSOR],
        },
      },
      select: { meetingId: true, entityId: true, entityType: true },
    })
  );
  const seen = new Set<string>();
  const unique: Array<{ entityId: string; entityType: ParticipantType }> = [];
  for (const batch of batches) {
    for (const row of batch) {
      const key = `${row.meetingId}:${row.entityId}`;
      if (seen.has(key)) continue;
      seen.add(key);
      unique.push(row);
    }
  }
  return unique;
}
